import {HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse} from '@angular/common/http';
import {inject} from '@angular/core';
import {ErrorService} from '@scalified-monorepo/ng-shared/errors';
import {Observable, throwError, timer} from 'rxjs';
import {catchError, retry, tap} from 'rxjs/operators';

/**
 * HTTP status codes that indicate a transient server/network unavailability
 * and should trigger a retry with a user-visible "Connecting..." toast.
 *
 * - 0:   Network error / server unreachable (e.g. backend restarting)
 * - 502: Bad Gateway
 * - 503: Service Unavailable
 * - 504: Gateway Timeout
 */
const RETRYABLE_STATUS_CODES = new Set([0, 502, 503, 504]);

const MAX_ATTEMPTS = 5;
const RETRY_DELAY_MS = 4000;

/**
 * Functional HTTP interceptor that transparently retries requests when the backend
 * is temporarily unavailable (e.g. during a nodemon restart in local development).
 *
 * Behavior:
 * - On the first retryable failure, sets `ErrorService.retryingState` to active so
 *   the app can show a single persistent "Connecting..." toast.
 * - On success after retrying: clears the retrying counter (toast dismissed when all done).
 * - After all attempts are exhausted: clears the counter and lets the error propagate
 *   to the existing GlobalErrorHandler which shows a normal error toast.
 *
 * Multiple concurrent requests that fail are tracked via a shared counter in ErrorService
 * so only one toast is ever shown regardless of how many requests are retrying.
 *
 * @example
 * ```typescript
 * // app.config.ts
 * provideHttpClient(
 *   withInterceptors([retryOnNetworkErrorInterceptor])
 * )
 * ```
 */
export function retryOnNetworkErrorInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const errorService = inject(ErrorService);

  // Preflight OPTIONS requests are browser-managed — their failure always
  // accompanies the actual request failing too, so skipping them avoids
  // a duplicate error toast when the backend is permanently down.
  if (req.method === 'OPTIONS') {
    return next(req);
  }

  // Track whether this specific request has entered a retry loop,
  // so we only call clearRetrying() for requests that actually retried.
  let hasRetried = false;

  return next(req).pipe(
    retry({
      count: MAX_ATTEMPTS,
      delay: (error, retryCount) => {
        const status: number = error?.status ?? -1;

        if (!RETRYABLE_STATUS_CODES.has(status)) {
          // Not a retryable error — propagate immediately
          return throwError(() => error);
        }

        hasRetried = true;
        errorService.emitRetrying(retryCount, MAX_ATTEMPTS);

        return timer(RETRY_DELAY_MS);
      },
    }),
    tap(event => {
      // Only clear on the final HttpResponse — not on intermediate events
      // like HttpSentEvent or HttpHeaderResponse which fire before the body arrives.
      if (hasRetried && event instanceof HttpResponse) {
        hasRetried = false;
        errorService.clearRetrying();
      }
    }),
    catchError(error => {
      // Retries exhausted or non-retryable — clear this request's slot and propagate
      if (hasRetried) {
        errorService.clearRetrying();
      }
      return throwError(() => error);
    })
  );
}
